import { Component, computed, Input, OnInit, Signal, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { ILanguageItem } from 'src/app/services/language/models/language.model';
import { IMenuItem } from './menu-item.model';
import { MenuItems } from './menu-items';
import * as _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { PopoverComponent } from '../popover/popover.component';
import { PopoverController, Platform, NavController, MenuController } from '@ionic/angular';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { LanguageService } from 'src/app/services/language/language.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { ReleaseResourceService } from 'src/app/services/release-resource/release-resource.service';
import { SafariViewController } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { SendLogsService } from 'src/app/services/logs/send-logs.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { UserService } from 'src/app/services/user/user.service';
import { take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { NetworkService } from 'src/app/services/network/network.service';
import { AuthService } from '@app/services/auth/auth.service';
import { LogoutService } from '@app/services/logout/logout.service';
import { ITenant } from '@app/services/tenant/models/tenant.model';
import { IUser } from '@app/services/user/models/user.model';
import { ITenantLite } from '@app/services/tenant/models/tenant-lite.model';
import { ErrorService } from '@app/services/error/error.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MenuComponent implements OnInit {

  @Input() content: any;

  public appVersion = '2025.3';
  public currentUrl: string;

  public isCordova: Signal<boolean> = this.platformService.isCordova;
  public isAndroid: Signal<boolean> = this.platformService.isAndroid;
  public isIPhone: Signal<boolean>  = this.platformService.isIPhone;
  public isIOS: Signal<boolean>     = this.platformService.isIOS;
  public isOnline: Signal<boolean> = this.networkService.hasInternetAccess;
  public user: Signal<IUser> = this.userService.userSignal;
  public tenant: Signal<ITenant> = this.tenantService.tenantSignal;
  public tenantList: Signal<ITenantLite[]> = this.tenantService.tenantListSignal;

  public languageItems: ILanguageItem[] = [];
  private logger: Logger;
  public menuItems: IMenuItem[] = _.cloneDeep(MenuItems);
  private securePrintLevel: string = null;
  private pagesToPushToNavigationStack: string[] = ['Printers', 'RegisterNfcTag', 'History'];
  public showLanguageMenu: boolean = false;
  private unSubscribe = new Subject<void>();
  public itemId: string;
  public printJobsPageType: string = 'printJobsPage';

  // locale map for help page links
  private readonly localeMap: { [key: string]: string } = {
    'ar': 'ar_EG',
    'da': 'da_DK',
    'de': 'de_DE',
    'en': 'en_US',
    'es': 'es_ES',
    'fi': 'fi_FI',
    'fr': 'fr_FR',
    'he': 'he_IL',
    'it': 'it_IT',
    'jp': 'ja_JA',
    'ko': 'ko_KR',
    'nl': 'nl_NL',
    'no': 'no_NO',
    'pl': 'pl_PL',
    'pt': 'pt_BR',
    'ro': 'ro_RO',
    'sv': 'sv_SE',
    'zh-cn': 'zh_CHS',
    'zh-tw': 'zh_CHT'
  };

  constructor(
    private authService: AuthService,
    private device: Device,
    private errorService: ErrorService,
    private inAppBrowser: InAppBrowser,
    private broadcaster: Broadcaster,
    private languageService: LanguageService,
    private loggingService: LoggingService,
    private logoutService: LogoutService,
    private menuController: MenuController,
    private navController: NavController,
    private networkService: NetworkService,
    private platform: Platform,
    private platformService: PlatformService,
    public popoverCtrl: PopoverController,
    private releaseResourceService: ReleaseResourceService,
    private router: Router,
    private safariViewController: SafariViewController,
    private sendLogsService: SendLogsService,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[MenuComponent]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.logger.info(methodName + ' APP_VERSION: ' + this.appVersion)


    broadcaster.on('securePrintLevel').subscribe((securePrintLevel: string) => {
      this.securePrintLevel = securePrintLevel;
    });

    broadcaster.on('printJobsPageType').subscribe((printJobsPageType: string) => {
      this.printJobsPageType = printJobsPageType;
    });
  }

  ngOnInit(): void {
    this.platform.ready().then(() => {
      this.logger.info('ngOnInit()');
      this.languageService.getLanguages()
        .subscribe((languageItems: ILanguageItem[]) => {
          this.languageItems = languageItems;
        }, (httpErrorResponse: HttpErrorResponse) => {
          this.errorService.handleHttpClientResponseError(httpErrorResponse, 'GET', 'MenuComponent - getLanguages')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        });
    });
  }

  public menuOnClose(): void {
    this.showLanguageMenu = false;
  }

  setAutoTestingId(menuItem: IMenuItem): string {
    return "AT-" + menuItem.title;
  }

  public languageItemClick(languageItem: ILanguageItem): void {
    this.languageService.changeLanguage(languageItem.codeLanguage);
    if (this.user()) {
      this.user().embedded.user.language = languageItem.codeLanguage;
      this.userService.saveLanguageChange(languageItem.codeLanguage, this.user()).subscribe(() => { });
    }
  }

  public isComponentActive(menuItem: IMenuItem): boolean {

    let urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {}
    let currentUrl = urlTree.toString();

    let showComponentAsActive = currentUrl === menuItem.route ? true : false;

    return showComponentAsActive;
  }

  public isLanguageActive(languageItem: ILanguageItem): boolean {
    return this.languageService.getCurrentLanguageCode() === languageItem.codeLanguage;
  }

  public async menuItemClick(event, menuItem: IMenuItem): Promise<void> {
    this.logger.info('(click) ' + menuItem.title);

    if (menuItem.title === 'SignIn' || menuItem.title === 'SignOut') {
      this.handleSignOut();
    } else if (menuItem.title === 'Help') {
      this.handleMenuHelp();
    } else if (menuItem.title === 'AirPrintPassword') {
      await this.handleAirPrintPassword();
    } else if (menuItem.title === 'PrinterIdMethod') {
      await this.handlePrinterIdMethod();
    } else if (menuItem.component) {
      this.handleComponentNavigation(menuItem);
    } else if (menuItem.languageMenuToggle) {
      this.showLanguageMenu = !this.showLanguageMenu;
    } else if (menuItem.title === 'Diagnostics') {
      await this.handleDiagnostics();
    }

    if (menuItem.title !== 'Language') {
      this.menuController.close();
    }
  }

  // ########################################################### //
  // ## menuItemClick functions ## //
  // ########################################################### //

   private handleSignOut(): void {
    this.logger.info('handleSignOut()');
    this.authService.deAuthenticateUser().pipe(take(1))
    .subscribe({
      next: () => {
        this.menuController.close().then(() => {
          this.logoutService.signOutUser().subscribe({
            next: () => {
              const queryParams = { signOutParam: 'USER_SIGN_OUT' };
              const path = this.isCordova() ? '/sign-in' : '/network-check';
              this.navController.navigateRoot([path], { queryParams });

            },
            error: (error) => {
              this.logger.error('SignOutUser error: ' + JSON.stringify(error));
              console.log('SignOutUser error', error);
            }
          });
        });
      },
      error: (error: HttpErrorResponse) => {
        this.logger.error('deAuthenticateUser error: ' + JSON.stringify(error));
        console.log('error', error);
      }
    });
  }

  private handleMenuHelp(): void {
    const currentLanguage = this.languageService.getCurrentLanguageCode();
    const locale = this.localeMap[currentLanguage] || 'en_US';
    this.logger.info('Show Help for language: ' + currentLanguage + ' (' + locale + ')');
    const url: string = `https://docshield.tungstenautomation.com/Printix/${locale}/help/user/`;
    if (this.isCordova()) {
      this.safariViewController.isAvailable()
        .then((available: boolean) => {
          if (available) {
            this.safariViewController.show({ url: url })
              .pipe(takeUntil(this.unSubscribe))
              .subscribe();
          } else {
            this.inAppBrowser.create(url, '_system');
          }
        });
    } else {
      this.logger.info('open manual');
      this.inAppBrowser.create(url);
    }
  }

  private async handleAirPrintPassword(): Promise<void> {
    let dataPackage: any = { type: 'airPrintPassword' };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      componentProps: { dataPackage },
      cssClass: 'showAirPrintPasswordsList'
    });

    await popover.present();

    await popover.onDidDismiss()
      .then(popoverData => {
        if (popoverData.data === 'airPrintPassword') {
          // Handle airPrintPassword
        }
      });
  }

  private async handlePrinterIdMethod(): Promise<void> {
    let dataPackage: any = {
      type: 'securePrintMethods',
      securePrintLevel: this.securePrintLevel
    };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      componentProps: { dataPackage },
      cssClass: 'secure-print-method'
    });

    await popover.present();

    await popover.onDidDismiss()
    .then(() => {
      let currentUrl = this.getrCurrentUrl();
      if (currentUrl === '/print-jobs' || currentUrl === '/recents') {
        this.broadcaster.broadcast('reload_page');
      }
    });
  }

  private getrCurrentUrl(): string {
    let urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {}
    let currentUrl = urlTree.toString();
    return currentUrl
  }

  private handleComponentNavigation(menuItem: IMenuItem): void {
    console.log('menuItem.title', menuItem.title);
    if (menuItem.route === '/print-jobs' || menuItem.route === '/capture') {
      this.navController.navigateBack(menuItem.route);
    } else {
      const param = menuItem.navParams ? menuItem.navParams : {};
      const queryParams = this.pagesToPushToNavigationStack.includes(menuItem.title)
        ? { resource: this.releaseResourceService.releaseResource }
        : menuItem.navParams;

        this.navController.navigateForward([menuItem.route], { queryParams });
    }
  }

  private async handleDiagnostics(): Promise<void> {
    let dataPackage: any = { type: 'sendLogs' };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      componentProps: { dataPackage },
      cssClass: 'send-logs'
    });

    await popover.present();

    await popover.onDidDismiss()
    .then((sendLogsParameters) => {
      if (sendLogsParameters && sendLogsParameters.data !== undefined && sendLogsParameters.data !== null) {
        switch (sendLogsParameters.data['method']) {
          case 'SendLogsWithEmail':
          this.sendLogsService.handleLogsForEmail();
          break;
        }
      }
    });
  }

// ########################################################### //
// ## ShowMenuItem ## //
// ########################################################### //

  public visibleMenuItems = computed(() => {
    this.logger.info('visibleMenuItems()');
    return this.menuItems.filter(menuItem => {

      // Always hide if no internet and item requires internet
      if (menuItem.showIfInternetIsAvailable && !this.isOnline()) {
        return false;
      }

      // Cordova logic
      // do not show Cordova items if not on cordova
      if (!this.isCordova() && !menuItem.showIfNotCordova) {
        return false;
      }

      // Authenticated logic
      const authenticatedLogic = this.setAuthenticatedLogic(menuItem);
      if (authenticatedLogic !== null) {return authenticatedLogic;}

      // Language menu toggle
      if (menuItem.languageMenuToggle) {
        return true;
      }

      // Special cases
      switch (menuItem.title) {
        case 'AirPrintPassword':
          return this.shouldShowAirPrintPassword();
        case 'RegisterNfcTag':
          return this.shouldShowRegisterNfcTag();
        case 'ChangeHome':
          return this.tenantList().length > 1;
      }

      // Default: show the item
      return true;
    });
  });

  private setAuthenticatedLogic(menuItem): boolean {
    // Show if both flags are set
    if (menuItem.showIfAuthenticated && menuItem.showIfNotAuthenticated) {
      return true;
    }

    // Hide if should be authenticated but not authenticated
    if (!this.authService.isAuthenticatedSignal() && menuItem.showIfAuthenticated) {
      return false;
    }

    // Hide if should be authenticated, is authenticated, but user not loaded
    if (
      this.authService.isAuthenticatedSignal() &&
      !this.user() &&
      menuItem.showIfAuthenticated
    ) {
      return menuItem.title === 'SignOut' ? true : false;
    }

    // Hide if should be not authenticated, but is authenticated
    if (this.authService.isAuthenticatedSignal() && menuItem.showIfNotAuthenticated) {
      return false;
    }

    // continue with other checks
    return null;
  }

  private shouldShowAirPrintPassword(): boolean {
    return this.tenant().tenantData.mobilePrint && this.isIOS();
  }

  private shouldShowRegisterNfcTag(): boolean {
    const userRole = this.user() ? this.user().embedded.permissions.role : null;
    if (!this.user() || userRole !== 'SYSTEM_MANAGER') {
      return false;
    }
    if (this.isAndroid()) {
      return true;
    }
    if (this.isIPhone()) {
      const deviceVersionNumber = parseInt(this.device.version, 10);
      return deviceVersionNumber > 12;
    }
    return false;
  }
}
